import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import paletteOutline from '../../../assets/images/palette-outline.svg'
import Loader from '../shared/Loader'
import {
  ExportHeader,
  CollectInfo,
  ErrorAlert,
  CustomExportForm,
  useExportPage
} from '../shared/Export'
import PdfOptionsModal from './PdfOptionsModal'
import {
  appendHiddenInputs,
  getResourcesFromUrl,
  resourceFilterInputs,
  singlePdfLimitMessage,
  splitSelection
} from '../ExportModal/utils/exportHelpers'
import { formatDate } from '../../utils/dateHelpers'

const EMPTY_PREFERENCES = {}

const PdfExport = ({
  collectId, draftId, exportUrl, returnUrl, savedPreferences,
  resourcesCount, singlePdfMaxResources, answersCount, singlePdfMaxAnswers
}) => {
  const [showOptionsModal, setShowOptionsModal] = useState(false)
  const resourcesLimit = singlePdfLimitMessage(resourcesCount, singlePdfMaxResources, 'ressources')
  const answersLimit = singlePdfLimitMessage(answersCount, singlePdfMaxAnswers, 'réponses')
  const singlePdfLimit = draftId ? resourcesLimit : answersLimit
  const {
    loading,
    error,
    data,
    ensemble,
    selectedQuestions,
    setSelectedQuestions,
    totalSelectedQuestions
  } = useExportPage({ collectId, preferencesKey: 'pdf', savedPreferences })

  const handleExport = useCallback(exportData => {
    const form = document.getElementById('export-form')
    const { sectionIds, questionIds } = splitSelection(selectedQuestions)

    appendHiddenInputs(form, [
      { name: 'section_ids', value: sectionIds.join(',') },
      { name: 'question_ids', value: questionIds.join(',') },
      { name: 'all_variables', value: exportData.withVariables },
      { name: 'export_as_zip', value: exportData.exportAsZip },
      ...resourceFilterInputs(getResourcesFromUrl())
    ])

    if (window.plausible)
      window.plausible('Collect PDF Exported')

    form.submit()
  }, [selectedQuestions])

  const handleSubmit = useCallback(event => {
    event.preventDefault()
    setShowOptionsModal(true)
  }, [])

  const handleModalClose = useCallback(() => setShowOptionsModal(false), [])

  const getRedirectBackUrl = useCallback(() => {
    if (returnUrl)
      return returnUrl
    if (draftId)
      return `/collects/${collectId}/drafts/${draftId}/resources`
    return '/collects'
  }, [collectId, draftId, returnUrl])

  const getExportUrl = useCallback(() => {
    if (exportUrl)
      return exportUrl
    return `/collects/${collectId}/drafts/${draftId}/resources_exports.pdf`
  }, [collectId, draftId, exportUrl])

  if (loading)
    return <Loader text="Chargement des données..." size="small" />

  if (error)
    return <ErrorAlert message={error} />

  if (!data)
    return null
  const currentDraft = draftId ? data.drafts.find(draftItem => draftItem.id === draftId) : null
  const startingAt = currentDraft?.starting_at ? formatDate(currentDraft.starting_at) : null

  return (
    <>
      <div className="modal-content border">
        <ExportHeader
          title="Personnaliser l'export PDF"
          icon={paletteOutline}
          iconAlt="Customize"
          cancelUrl={getRedirectBackUrl()}
        />
        <div className="modal-body">
          <CollectInfo
            collectName={data.name}
            startingAt={startingAt}
            ensembleName={ensemble?.name}
          />
          <CustomExportForm
            exportUrl={getExportUrl()}
            sections={data.sections}
            selectedQuestions={selectedQuestions}
            title="Sélectionnez les sections et questions à exporter au niveau de la collecte"
            onQuestionChange={setSelectedQuestions}
            onSubmit={handleSubmit}
          />
        </div>
        <div className="modal-footer">
          <div className="d-flex justify-content-end align-items-center w-100">
            <button
              type="submit"
              form="export-form"
              className="btn btn-success"
              disabled={totalSelectedQuestions === 0}
            >
              Suivant
            </button>
          </div>
        </div>
      </div>
      {showOptionsModal && data ? (
        <PdfOptionsModal
          collect={data}
          startingAt={startingAt}
          savedPreferences={savedPreferences.pdf || EMPTY_PREFERENCES}
          singlePdfLimit={singlePdfLimit}
          onClose={handleModalClose}
          onExport={handleExport}
        />
      ) : null}
    </>
  )
}

PdfExport.defaultProps = {
  answersCount: null,
  draftId: null,
  exportUrl: null,
  resourcesCount: null,
  returnUrl: null,
  savedPreferences: EMPTY_PREFERENCES,
  singlePdfMaxAnswers: null,
  singlePdfMaxResources: null
}

PdfExport.propTypes = {
  answersCount: PropTypes.number,
  collectId: PropTypes.number.isRequired,
  draftId: PropTypes.number,
  exportUrl: PropTypes.string,
  resourcesCount: PropTypes.number,
  returnUrl: PropTypes.string,
  savedPreferences: PropTypes.shape({ pdf: PropTypes.shape({}) }),
  singlePdfMaxAnswers: PropTypes.number,
  singlePdfMaxResources: PropTypes.number
}

export default PdfExport
